import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  Typography,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Description as DescriptionIcon,
  DirectionsCar as CarIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { formatDate, formatPhoneNumber, formatStatusLabel } from '../utils/helpers';

const InfoRow = ({ icon, label, value }) => {
  if (!value) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.25 }}>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const DriverDetailsModal = ({ open, onClose, driver }) => {
  if (!driver) return null;

  const driverProfileImageUrl =
    driver.profilePictureUrl ||
    driver.profilePicture ||
    driver.profileImageUrl ||
    driver.profileImage ||
    driver.profilePicUrl ||
    driver.photoURL ||
    driver.photoUrl ||
    '';

  const initials = driver.name
    ? driver.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')
    : 'DR';

  const statusColor = driver.approved
    ? 'success'
    : driver.status === 'rejected'
      ? 'error'
      : 'warning';

  const statusLabel = driver.approved
    ? 'Approved'
    : formatStatusLabel(driver.status, 'Pending');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Driver Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box
            sx={{
              minWidth: { xs: '100%', md: 240 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Avatar
              src={driverProfileImageUrl}
              sx={{
                width: 120,
                height: 120,
                fontSize: 42,
                fontWeight: 700,
                bgcolor: '#f5f5f5',
                border: '3px solid #e0e0e0',
                mb: 1.5,
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {driver.name || 'Unnamed Driver'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {driver.driverId || 'No Driver ID'}
            </Typography>
            <Chip label={statusLabel} color={statusColor} size="small" sx={{ fontWeight: 600 }} />
            {driverProfileImageUrl && (
              <Typography
                component="a"
                href={driverProfileImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                sx={{ mt: 1.25, color: '#DD2424', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                View Uploaded Image
              </Typography>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<EmailIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="Email"
                  value={driver.email || 'N/A'}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<PhoneIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="Phone"
                  value={formatPhoneNumber(driver.phone)}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<BadgeIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="Driver ID"
                  value={driver.driverId || 'N/A'}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<DescriptionIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="License Number"
                  value={driver.licenseNumber || 'N/A'}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<CalendarIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="Available Until"
                  value={driver.availableUntil ? formatDate(driver.availableUntil) : 'Not set'}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <InfoRow
                  icon={<CalendarIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                  label="Joined"
                  value={formatDate(driver.createdAt)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Vehicle Information
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow
                icon={<CarIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                label="Own Vehicle"
                value={
                  driver.ownVehicle
                    ? `${driver.ownVehicle.type || ''} ${driver.ownVehicle.model || ''} ${driver.ownVehicle.year ? `(${driver.ownVehicle.year})` : ''}`.trim() ||
                      'Available'
                    : 'Not provided'
                }
              />
              <InfoRow
                icon={<CarIcon sx={{ color: '#DD2424', fontSize: 20, mt: 0.25 }} />}
                label="Assigned Vehicle"
                value={
                  driver.assignedVehicle
                    ? `${driver.assignedVehicle.type || ''} ${driver.assignedVehicle.model || ''} ${driver.assignedVehicle.plateNumber ? `- ${driver.assignedVehicle.plateNumber}` : ''}`.trim() ||
                      'Assigned'
                    : 'Not assigned'
                }
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverDetailsModal;